import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowPathIcon,
  BeakerIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ServerStackIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { api } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const WINDOWS = [1, 6, 12, 24, 48];

const VERDICT_STYLES = {
  healthy: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  needs_attention: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  blocked: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  unknown: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};

const VERDICT_LABEL = {
  healthy: 'Healthy',
  needs_attention: 'Needs attention',
  blocked: 'Blocked',
  unknown: 'Unknown',
};

const FLAG_STYLES = {
  error: 'text-red-600 dark:text-red-400',
  warn: 'text-amber-600 dark:text-amber-400',
  info: 'text-slate-600 dark:text-slate-400',
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const timeAgo = (value) => {
  if (!value) return '—';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const secs = Math.floor(ms / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
};

const fmt = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
};

const fmtRate = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `${Number(n).toLocaleString()}/h`;
};

const StatCard = ({ label, value, sub, accent, isDarkMode }) => (
  <div className={`rounded-xl border p-4 ${isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200'}`}>
    <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
    <p className={`mt-1 text-2xl font-semibold tabular-nums ${accent || (isDarkMode ? 'text-white' : 'text-slate-900')}`}>{value}</p>
    {sub && <p className={`mt-1 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{sub}</p>}
  </div>
);

const TrendTable = ({ trends, isDarkMode }) => {
  if (!trends) return null;
  const cell = (row, h) => {
    const w = row?.[`${h}h`];
    if (!w || w.count == null) return '—';
    return `${fmt(w.count)} (${fmtRate(w.perHour)})`;
  };
  const rows = [
    { key: 'follows', label: 'Follows (ledger)' },
    { key: 'followbacks', label: 'Followbacks' },
    { key: 'newConversations', label: 'New conversations' },
    { key: 'botReplies', label: 'Bot replies' },
    { key: 'ctas', label: 'CTAs shared' },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
            <th className="text-left py-2 pr-4 font-medium">Metric</th>
            {WINDOWS.map((h) => (
              <th key={h} className="text-right py-2 px-2 font-medium tabular-nums">{h}h</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
              <td className={`py-2 pr-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{r.label}</td>
              {WINDOWS.map((h) => (
                <td key={h} className={`py-2 px-2 text-right tabular-nums ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                  {cell(trends[r.key], h)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OpsHealthTab = () => {
  const { isDarkMode } = useTheme();
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [check, setCheck] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      setHistoryLoading(true);
      const res = await api.get('/ops-health/checks', { params: { limit: 12 } });
      setHistory(res.data?.checks || []);
    } catch (err) {
      // non-fatal
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadCheck = async (id) => {
    try {
      setError(null);
      const res = await api.get(`/ops-health/checks/${id}`);
      setCheck(res.data?.check || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load check');
    }
  };

  const runCheck = async () => {
    try {
      setRunning(true);
      setError(null);
      const res = await api.post('/ops-health/run', {});
      setCheck(res.data?.check || null);
      loadHistory();
    } catch (err) {
      setError(err.response?.data?.error || 'Ops health check failed');
    } finally {
      setRunning(false);
    }
  };

  const verdict = check?.verdict || 'unknown';
  const capture = check?.ledger?.liveCapture?.captureRate;
  const fb24 = check?.followback?.windows?.['24h'];
  const unreplied = check?.chatbot?.unreplied?.total;
  const mongoSlow = check?.mongo?.slowCount;

  return (
    <div className="space-y-6">
      <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2">
              <BeakerIcon className={`w-6 h-6 ${isDarkMode ? 'text-sky-400' : 'text-sky-600'}`} />
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Ops Health Check
              </h2>
            </div>
            <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              One-shot sweep across ledger capture (VPS follows vs Mongo), followback→DM delivery on VPS 1–2,
              unreplied chatbot conversations, volume trends (1h–48h), and Mongo pressure.
            </p>
          </div>
          <button
            type="button"
            onClick={runCheck}
            disabled={running}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white
              ${running ? 'bg-sky-400 cursor-wait' : 'bg-sky-600 hover:bg-sky-500'} transition-colors`}
          >
            {running ? (
              <>
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Running sweep…
              </>
            ) : (
              <>
                <PlayIcon className="w-4 h-4" />
                Run health check
              </>
            )}
          </button>
        </div>

        {error && (
          <div className={`mt-4 flex items-start gap-2 rounded-lg border px-3 py-2 text-sm
            ${isDarkMode ? 'border-red-800 bg-red-950/40 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>
            <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {running && (
          <p className={`mt-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Querying Mongo across several windows — usually 15–40 seconds.
          </p>
        )}
      </div>

      {check && (
        <>
          {/* Verdict strip */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex flex-wrap items-center gap-3">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ${VERDICT_STYLES[verdict] || VERDICT_STYLES.unknown}`}>
                {verdict === 'healthy' && <CheckCircleIcon className="w-4 h-4" />}
                {verdict === 'needs_attention' && <ExclamationTriangleIcon className="w-4 h-4" />}
                {verdict === 'blocked' && <XCircleIcon className="w-4 h-4" />}
                {VERDICT_LABEL[verdict] || verdict}
              </span>
              <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {check.summary}
              </span>
              <span className={`ml-auto text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                {formatDateTime(check.finishedAt)} · {(check.durationMs / 1000).toFixed(1)}s
              </span>
            </div>

            {check.agents?.length > 0 && (
              <p className={`mt-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Scoped agents:{' '}
                {check.agents.map((a) => a.name).join(' · ')}
              </p>
            )}

            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard
                label="Ledger capture"
                value={capture != null ? `${capture}%` : '—'}
                sub={check.ledger?.liveCapture
                  ? `gap ${fmt(check.ledger.liveCapture.gap)} · ${check.ledger.liveCapture.runningJobs} jobs`
                  : null}
                accent={capture != null && capture < 95 ? 'text-amber-500' : undefined}
                isDarkMode={isDarkMode}
              />
              <StatCard
                label="FB → DM (24h)"
                value={fb24?.pct != null ? `${fb24.pct}%` : '—'}
                sub={fb24 ? `${fmt(fb24.dmSent)}/${fmt(fb24.detected)} · miss ${fmt(fb24.missing)}` : null}
                accent={fb24?.pct != null && fb24.pct < 97 ? 'text-amber-500' : undefined}
                isDarkMode={isDarkMode}
              />
              <StatCard
                label="Unreplied (24h)"
                value={fmt(unreplied)}
                sub="excl. last 2 min (in-flight)"
                accent={unreplied > 25 ? 'text-amber-500' : undefined}
                isDarkMode={isDarkMode}
              />
              <StatCard
                label="Mongo slow ops"
                value={mongoSlow != null ? fmt(mongoSlow) : '—'}
                sub={check.mongo?.ok
                  ? `${fmt(check.mongo.connections?.current)} conns · ${fmt(check.mongo.memMb?.resident)} MB`
                  : check.mongo?.error || 'probe failed'}
                isDarkMode={isDarkMode}
              />
            </div>

            {check.flags?.length > 0 && (
              <ul className="mt-4 space-y-1">
                {check.flags.map((f, i) => (
                  <li key={`${f.code}-${i}`} className={`text-sm ${FLAG_STYLES[f.level] || FLAG_STYLES.info}`}>
                    · {f.message}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Trends */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Volume trends
            </h3>
            <TrendTable trends={check.trends} isDarkMode={isDarkMode} />
            {check.ledger?.last15Min && (
              <p className={`mt-3 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Last 15 min ledger writes: {fmt(check.ledger.last15Min.count)}
                {' '}({check.ledger.last15Min.perMin}/min)
              </p>
            )}
          </div>

          {/* Followback detail */}
          <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Followback → DM by window
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                    <th className="text-left py-2 pr-4 font-medium">Window</th>
                    <th className="text-right py-2 px-2 font-medium">Detected</th>
                    <th className="text-right py-2 px-2 font-medium">DM'd</th>
                    <th className="text-right py-2 px-2 font-medium">Missing</th>
                    <th className="text-right py-2 px-2 font-medium">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {WINDOWS.map((h) => {
                    const w = check.followback?.windows?.[`${h}h`] || {};
                    return (
                      <tr key={h} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                        <td className="py-2 pr-4">{h}h</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(w.detected)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(w.dmSent)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(w.missing)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{w.pct != null ? `${w.pct}%` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {check.followback?.perAgent24h?.length > 0 && (
              <div className="mt-4">
                <h4 className={`text-xs font-medium uppercase tracking-wide mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Per agent (24h)
                </h4>
                <div className="space-y-1">
                  {check.followback.perAgent24h.map((a) => (
                    <div key={a.agentId} className={`text-sm flex justify-between gap-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      <span>{a.agentName}</span>
                      <span className="tabular-nums">
                        {fmt(a.dmSent)}/{fmt(a.detected)} ({a.pct != null ? `${a.pct}%` : '—'})
                        {a.missing > 0 ? ` · miss ${a.missing}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {check.followback?.missingSamples?.length > 0 && (
              <div className="mt-4">
                <h4 className={`text-xs font-medium uppercase tracking-wide mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Missing DM samples (24h)
                </h4>
                <ul className={`text-sm space-y-1 font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {check.followback.missingSamples.slice(0, 10).map((m) => (
                    <li key={`${m.username}-${m.followedBackAt}`}>
                      @{m.username} · {m.agentName || m.agentId} · {timeAgo(m.followedBackAt)}
                      {m.dmAttemptCount ? ` · attempts ${m.dmAttemptCount}` : ' · no attempt'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Unreplied + Mongo */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Unreplied conversations
              </h3>
              <p className={`text-sm mb-3 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Active chats where the last message is from the user (last 24h, excluding &lt;2m).
              </p>
              {check.chatbot?.unreplied?.buckets && (
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {[
                    ['lt1h', '<1h'],
                    ['1h_6h', '1–6h'],
                    ['6h_24h', '6–24h'],
                  ].map(([key, label]) => (
                    <div key={key} className={`rounded-lg border p-2 text-center ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                      <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{label}</p>
                      <p className={`text-lg font-semibold tabular-nums ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {fmt(check.chatbot.unreplied.buckets[key] || 0)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {check.chatbot?.unreplied?.sample?.length > 0 && (
                <ul className={`text-xs space-y-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  {check.chatbot.unreplied.sample.slice(0, 6).map((s) => (
                    <li key={s.id} className="truncate">
                      {timeAgo(s.lastActivity)} · msgs {s.messagesCount} · “{s.preview}”
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center gap-2 mb-3">
                <ServerStackIcon className={`w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`} />
                <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                  Mongo pressure
                </h3>
              </div>
              {check.mongo?.ok ? (
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Connections</dt>
                    <dd className="tabular-nums">{fmt(check.mongo.connections?.current)} / {fmt(check.mongo.connections?.available)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Resident memory</dt>
                    <dd className="tabular-nums">{fmt(check.mongo.memMb?.resident)} MB</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Active ops</dt>
                    <dd className="tabular-nums">{fmt(check.mongo.activeOps)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>Slow (≥1s)</dt>
                    <dd className="tabular-nums">{fmt(check.mongo.slowCount)}</dd>
                  </div>
                  {check.mongo.slowOps?.length > 0 && (
                    <ul className={`mt-2 text-xs space-y-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                      {check.mongo.slowOps.slice(0, 5).map((o, i) => (
                        <li key={i}>{o.secs}s · {o.op} · {o.ns || '?'}</li>
                      ))}
                    </ul>
                  )}
                </dl>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                  {check.mongo?.error || 'Mongo probe unavailable'}
                </p>
              )}

              {check.organic?.windows?.['24h'] && (
                <p className={`mt-4 text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  Organic (non-seeded) 24h: {fmt(check.organic.windows['24h'].dmSent)}/
                  {fmt(check.organic.windows['24h'].newOrganic)} DM'd
                  {check.organic.windows['24h'].missing
                    ? ` · miss ${check.organic.windows['24h'].missing}`
                    : ' · none missing'}
                </p>
              )}
            </div>
          </div>

          {/* Live job capture */}
          {check.ledger?.liveCapture?.perJob?.length > 0 && (
            <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Running job capture (cumulative VPS vs ledger)
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                      <th className="text-left py-2 pr-4 font-medium">Agent</th>
                      <th className="text-right py-2 px-2 font-medium">VPS</th>
                      <th className="text-right py-2 px-2 font-medium">Ledger</th>
                      <th className="text-right py-2 px-2 font-medium">Capture</th>
                      <th className="text-right py-2 px-2 font-medium">Gap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {check.ledger.liveCapture.perJob.map((j) => (
                      <tr key={j.jobId} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
                        <td className="py-2 pr-4">{j.agentName}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(j.vps)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(j.ledger)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{j.captureRate != null ? `${j.captureRate}%` : '—'}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{fmt(j.gap)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* History */}
      <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200'}`}>
        <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Recent checks
        </h3>
        {historyLoading && (
          <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>Loading…</p>
        )}
        {!historyLoading && history.length === 0 && (
          <p className={`text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            No checks yet — run one above.
          </p>
        )}
        <ul className="space-y-2">
          {history.map((h) => (
            <li key={h.id || h._id}>
              <button
                type="button"
                onClick={() => loadCheck(h.id || h._id)}
                className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition-colors
                  ${isDarkMode
                    ? 'border-slate-700 hover:bg-slate-800 text-slate-300'
                    : 'border-slate-200 hover:bg-slate-50 text-slate-700'}`}
              >
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium mr-2 ${VERDICT_STYLES[h.verdict] || VERDICT_STYLES.unknown}`}>
                  {VERDICT_LABEL[h.verdict] || h.verdict}
                </span>
                {timeAgo(h.finishedAt || h.startedAt)}
                {h.durationMs != null && (
                  <span className={`ml-2 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    · {(h.durationMs / 1000).toFixed(1)}s
                  </span>
                )}
                {h.summary && (
                  <span className={`block mt-1 text-xs truncate ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {h.summary}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default OpsHealthTab;
